import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { Select } from '@/components/Controls/Select';
import { useGetEquipmentsSelectOptions } from '@/hooks/equipment.hooks';
import {
  useGetDamageByNameAndSeverity,
  useGetUniqueDamageNamesSelectOptions,
} from '@/hooks/damage.hooks';
import { useGetRepairServicesSelectOptions } from '@/hooks/repair-service.hooks';
import { useGetMatchingRepairCostingError } from '@/hooks/repair-costing.hooks';
import { FieldError } from '@/type/error.types';
import { Loader } from '@/components/Loading/Loader';

export const RepairCostingInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  /**
   * === Set Value Watchers section ===
   */
  const equipmentIdValue = watch && watch('equipmentId' as Path<TSchema>);
  const damageIdValue = watch && watch('damageId' as Path<TSchema>);
  const [damageName, setDamageName] = useState<string>('');
  const [damageSeverity, setDamageSeverity] = useState<number | ''>('');
  const repairServiceIdValue =
    watch && watch('repairServiceId' as Path<TSchema>);
  const activeValue = watch && watch('active' as Path<TSchema>);
  const [costDisplay, setCostDisplay] = useState<string>('');
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: matchingRepairCostingError } = useGetMatchingRepairCostingError(
    {
      id: data?.id,
      equipmentId: equipmentIdValue,
      damageId: damageIdValue,
      repairServiceId: repairServiceIdValue,
    },
  );

  /**
   * === Fetch Options section ===
   */
  const { getAllEquipmentsSelectOptions, isLoadingAllEquipments } =
    useGetEquipmentsSelectOptions();

  const { getAllDamageNamesSelectOptions, isLoadingAllDamages } =
    useGetUniqueDamageNamesSelectOptions();

  const { getAllRepairServicesSelectOptions, isLoadingAllRepairServices } =
    useGetRepairServicesSelectOptions();

  const { data: damageByNameAndSeverity } = useGetDamageByNameAndSeverity(
    damageName,
    damageSeverity as number,
  );

  useEffect(() => {
    if (damageByNameAndSeverity?.data?.id) {
      setValue(
        'damageId' as Path<TSchema>,
        damageByNameAndSeverity?.data?.id as PathValue<TSchema, Path<TSchema>>,
      );
    }
  }, [damageByNameAndSeverity, setValue]);
  /**
   * === END OF Fetch Options section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (equipmentIdValue) {
      resetFieldError<TSchema>({ fieldName: 'equipmentId', setError });
    }
    if (damageName) {
      resetFieldError<TSchema>({ fieldName: 'damageName', setError });
    }
    if (damageSeverity) {
      resetFieldError<TSchema>({ fieldName: 'damageSeverity', setError });
    }
    if (repairServiceIdValue) {
      resetFieldError<TSchema>({ fieldName: 'repairServiceId', setError });
    }
    if (costDisplay) {
      resetFieldError<TSchema>({ fieldName: 'cost', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    equipmentIdValue,
    damageName,
    damageSeverity,
    repairServiceIdValue,
    costDisplay,
  ]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('equipmentId' as Path<TSchema>, data.equipmentId ?? '');
      setValue('damageId' as Path<TSchema>, data.damageId ?? '');
      setDamageName(data.damage?.name ?? '');
      setDamageSeverity(data.damage?.severity ?? '');
      setValue('repairServiceId' as Path<TSchema>, data.repairServiceId ?? '');
      setValue('cost' as Path<TSchema>, data.cost ?? '');
      setCostDisplay(data.cost != null ? String(data.cost) : null);
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let hasError = false;

    if (!damageName) {
      setError('damageName' as Path<TSchema>, {
        message: t('damageRequired'),
      });
      hasError = true;
    }

    if (!damageSeverity) {
      setError('damageSeverity' as Path<TSchema>, {
        message: t('damageSeverityRequired'),
      });
      hasError = true;
    }

    if (hasError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const repairCostingErrorMessages =
      matchingRepairCostingError?.data?.errorMessages;

    if (
      repairCostingErrorMessages?.includes(
        FieldError.EQUIPMENT_DAMAGE_REPAIR_SERVICE_EXISTS,
      )
    ) {
      setError('equipmentId' as Path<TSchema>, {
        message: t('equipmentDamageRepairServiceAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('theRepairCosting')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <>
            <FormField
              text={t('common:equipment')}
              required
              textWrapperClassName="mt-3 max-md:mt-0"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Select
                  {...(register && register('equipmentId' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.equipmentId?.message as string)
                  }
                  placeholder={t('common:chooseAnEquipment')}
                  options={getAllEquipmentsSelectOptions() || []}
                  value={equipmentIdValue}
                  onChange={(e) =>
                    setValue &&
                    setValue(
                      'equipmentId' as Path<TSchema>,
                      e.target.value as PathValue<TSchema, Path<TSchema>>,
                    )
                  }
                />
              </div>
            </FormField>
            <FormField
              text={t('common:damage')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Select
                  errorMessage={
                    errors && (errors.damageName?.message as string)
                  }
                  placeholder={t('common:chooseADamage')}
                  options={getAllDamageNamesSelectOptions() || []}
                  value={damageName}
                  onChange={(e) => setDamageName(e.target.value)}
                />
              </div>
            </FormField>
            <FormField
              text={t('damageSeverity')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Select
                  errorMessage={
                    errors && (errors.damageSeverity?.message as string)
                  }
                  placeholder={t('chooseADamageSeverity')}
                  options={[
                    { label: '1', value: 1 },
                    { label: '2', value: 2 },
                    { label: '3', value: 3 },
                  ]}
                  value={damageSeverity}
                  onChange={(e) =>
                    setDamageSeverity(
                      e.target.value ? Number(e.target.value) : '',
                    )
                  }
                />
              </div>
            </FormField>
            <FormField
              text={t('common:repairService')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Select
                  {...(register &&
                    register('repairServiceId' as Path<TSchema>))}
                  errorMessage={
                    errors && (errors.repairServiceId?.message as string)
                  }
                  placeholder={t('chooseARepairService')}
                  options={getAllRepairServicesSelectOptions() || []}
                  value={repairServiceIdValue}
                  onChange={(e) =>
                    setValue &&
                    setValue(
                      'repairServiceId' as Path<TSchema>,
                      e.target.value as PathValue<TSchema, Path<TSchema>>,
                    )
                  }
                />
              </div>
            </FormField>
            <FormField
              text={t('common:costInEuros')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Input
                  errorMessage={errors && (errors.cost?.message as string)}
                  placeholder={t('common:costInEuros')}
                  type="text"
                  inputMode="decimal"
                  value={costDisplay}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setCostDisplay(val);
                      const num = parseFloat(val);
                      setValue(
                        'cost' as Path<TSchema>,
                        (isNaN(num) ? '' : num) as PathValue<
                          TSchema,
                          Path<TSchema>
                        >,
                      );
                    }
                  }}
                />
              </div>
            </FormField>
            <FormField
              text={t('common:activeStatus')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                <Switch
                  {...(register && register('active' as Path<TSchema>))}
                  errorMessage={errors && (errors.active?.message as string)}
                  checked={data ? activeValue : true}
                />
              </div>
            </FormField>
          </>
        </Card>
      </form>
      {(isLoadingAllEquipments ||
        isLoadingAllDamages ||
        isLoadingAllRepairServices) && <Loader />}
    </>
  );
};
